import { supabase } from './supabaseClient';

/**
 * AI Training Service
 * Handles document upload, chat with RAG, and analytics
 */

// ══════════════════════════════════════════════════════════════
// Training Documents Management
// ══════════════════════════════════════════════════════════════

/**
 * Upload training document to Supabase Storage
 */
export async function uploadTrainingDocument(file, title, currentUser) {
  if (!supabase) throw new Error('Supabase not configured');
  
  try {
    // Generate unique filename
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `training/${fileName}`;
    
    // Upload to Supabase Storage
    const { error: uploadError } = await supabase.storage
      .from('documents')
      .upload(filePath, file);
    
    if (uploadError) throw uploadError;
    
    // Get public URL
    const { data: urlData } = supabase.storage
      .from('documents')
      .getPublicUrl(filePath);
    
    // Create database record
    const { data: doc, error: dbError } = await supabase
      .from('training_documents')
      .insert([{
        title,
        file_url: urlData.publicUrl,
        file_type: fileExt,
        uploaded_by: currentUser.id,
        status: 'pending'
      }])
      .select()
      .single();
    
    if (dbError) throw dbError;
    
    // Trigger document processing (call edge function)
    await processDocument(doc.id);
    
    return doc;
  } catch (err) {
    console.error('❌ Error uploading training document:', err);
    throw err;
  }
}

/**
 * Trigger document processing via Edge Function
 */
async function processDocument(documentId) {
  try {
    const { data, error } = await supabase.functions.invoke('process-document', {
      body: { documentId }
    });
    
    if (error) throw error;
    
    return data;
  } catch (err) {
    console.error('❌ Error processing document:', err);
    throw err;
  }
}

/**
 * Get all training documents
 */
export async function getTrainingDocuments() {
  if (!supabase) return [];
  
  try {
    const { data, error } = await supabase
      .from('training_documents')
      .select(`
        *,
        uploader:users(name, email)
      `)
      .order('created_at', { ascending: false });
    
    if (error) throw error;
    
    return data || [];
  } catch (err) {
    console.error('❌ Error fetching training documents:', err);
    return [];
  }
}

/**
 * Delete training document
 */
export async function deleteTrainingDocument(documentId) {
  if (!supabase) return false;
  
  try {
    const { error } = await supabase
      .from('training_documents')
      .delete()
      .eq('id', documentId);
    
    if (error) throw error;
    
    return true;
  } catch (err) {
    console.error('❌ Error deleting training document:', err);
    return false;
  }
}

// ══════════════════════════════════════════════════════════════
// AI Chat with RAG
// ══════════════════════════════════════════════════════════════

/**
 * Send message to AI Mentor with role-based context
 */
export async function sendAIMessage(question, sessionId, currentUser, conversationHistory = [], mode = 'chat', documentId = null) {
  if (!supabase) throw new Error('Supabase not configured');
  if (!currentUser) throw new Error('User not authenticated');
  
  try {
    // Call Edge Function for AI chat
    const { data, error } = await supabase.functions.invoke('ai-chat', {
      body: {
        question,
        sessionId,
        userContext: {
          id: currentUser.id,
          name: currentUser.name,
          role: currentUser.role,
          email: currentUser.email
        },
        conversationHistory,
        mode,
        documentId
      }
    });
    
    if (error) throw error;
    
    return data; // { answer, sessionId, tokensUsed, contextChunks, remainingQuestions }
  } catch (err) {
    console.error('❌ Error sending AI message:', err);
    throw err;
  }
}

/**
 * Get user's chat history for a session
 */
export async function getChatHistory(userId, sessionId) {
  if (!supabase) return [];
  
  try {
    const { data, error } = await supabase
      .from('ai_chat_logs')
      .select('*')
      .eq('user_id', userId)
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });
    
    if (error) throw error;
    
    return data || [];
  } catch (err) {
    console.error('❌ Error fetching chat history:', err);
    return [];
  }
}

/**
 * Get user's recent sessions
 */
export async function getRecentSessions(userId, limit = 10) {
  if (!supabase) return [];
  
  try {
    const { data, error } = await supabase
      .from('ai_chat_logs')
      .select('session_id, created_at, role')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    
    // Group by session_id and get first/last message time
    const sessions = {};
    data.forEach(log => {
      if (!sessions[log.session_id]) {
        sessions[log.session_id] = {
          sessionId: log.session_id,
          startTime: log.created_at,
          endTime: log.created_at,
          role: log.role,
          messageCount: 0
        };
      }
      sessions[log.session_id].endTime = log.created_at;
      sessions[log.session_id].messageCount++;
    });
    
    return Object.values(sessions).sort((a, b) => 
      new Date(b.endTime) - new Date(a.endTime)
    );
  } catch (err) {
    console.error('❌ Error fetching recent sessions:', err);
    return [];
  }
}

// ══════════════════════════════════════════════════════════════
// Admin Analytics
// ══════════════════════════════════════════════════════════════

/**
 * Get all chat logs (admin only)
 */
export async function getAllChatLogs(limit = 100) {
  if (!supabase) return [];
  
  try {
    const { data, error } = await supabase
      .from('ai_chat_logs')
      .select(`
        *,
        user:users(name, email, role)
      `)
      .order('created_at', { ascending: false })
      .limit(limit);
    
    if (error) throw error;
    
    return data || [];
  } catch (err) {
    console.error('❌ Error fetching all chat logs:', err);
    return [];
  }
}

/**
 * Get training analytics summary
 */
export async function getTrainingAnalytics() {
  if (!supabase) return null;
  
  try {
    // Get total questions asked
    const { count: totalQuestions } = await supabase
      .from('ai_chat_logs')
      .select('*', { count: 'exact', head: true });
    
    // Get unique users who asked questions
    const { count: activeUsers } = await supabase
      .from('ai_chat_logs')
      .select('user_id', { count: 'exact', head: true })
      .not('user_id', 'is', null);
    
    // Get total documents
    const { count: totalDocs } = await supabase
      .from('training_documents')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'ready');
    
    // Get most active users
    const { data: topUsers } = await supabase
      .from('ai_chat_logs')
      .select('user_id, count(*)')
      .group('user_id')
      .order('count', { ascending: false })
      .limit(5);
    
    return {
      totalQuestions: totalQuestions || 0,
      activeUsers: activeUsers || 0,
      totalDocs: totalDocs || 0,
      topUsers: topUsers || []
    };
  } catch (err) {
    console.error('❌ Error fetching training analytics:', err);
    return null;
  }
}
